import { useState } from "react";
import { MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine, Eyebrow } from "@/components/Reveal";
import { CONTACT } from "@/data/site";

const NEXT_STEPS = [
  {
    number: "01",
    title: "Send your request",
    text: "Complete the form and your email application will open with your request ready to send.",
  },
  {
    number: "02",
    title: "We respond",
    text: "The office will contact you to arrange a convenient time for your consultation.",
  },
  {
    number: "03",
    title: "Meet your attorney",
    text: "Discuss your matter in confidence and leave with a clear understanding of the way forward.",
  },
];

const inputClass =
  "w-full bg-transparent border-b border-navy/25 focus:border-gold-hover outline-none py-3 text-navy placeholder:text-muted-foreground/60 transition-colors duration-300";

const ConsultationForm = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    practiceArea: "",
    message: "",
  });

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `Consultation Request - ${form.practiceArea} - ${form.firstName} ${form.lastName}`
    );
    const body = encodeURIComponent(
      `CONSULTATION REQUEST\n\n` +
        `Name: ${form.firstName} ${form.lastName}\n\n` +
        `Email: ${form.email}\n\n` +
        `Telephone: ${form.phone}\n\n` +
        `Area of Law: ${form.practiceArea}\n\n` +
        `Brief Description:\n${form.message}\n\n` +
        `Submitted through the Annwil Law Ja website.`
    );
    toast.success("Your email application should open with your consultation request ready to send.");
    window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
  };

  return (
    <form onSubmit={handleSubmit} data-testid="consultation-form" className="bg-card border border-navy/10 p-8 md:p-12">
      <div className="grid sm:grid-cols-2 gap-8">
        <div>
          <label htmlFor="firstName" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">First Name</label>
          <input id="firstName" data-testid="form-first-name" type="text" required value={form.firstName} onChange={update("firstName")} className={inputClass} />
        </div>
        <div>
          <label htmlFor="lastName" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">Last Name</label>
          <input id="lastName" data-testid="form-last-name" type="text" required value={form.lastName} onChange={update("lastName")} className={inputClass} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-8 mt-8">
        <div>
          <label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">Email Address</label>
          <input id="email" data-testid="form-email" type="email" required value={form.email} onChange={update("email")} className={inputClass} />
        </div>
        <div>
          <label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">Telephone</label>
          <input id="phone" data-testid="form-phone" type="tel" value={form.phone} onChange={update("phone")} className={inputClass} />
        </div>
      </div>
      <div className="mt-8">
        <label htmlFor="practiceArea" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">Area of Law</label>
        <select id="practiceArea" data-testid="form-practice-area" required value={form.practiceArea} onChange={update("practiceArea")} className={inputClass}>
          <option value="">Select an option</option>
          <option>Family Law</option>
          <option>Real Estate Law</option>
          <option>Probate</option>
          <option>Estate Planning</option>
          <option>Other / Not Sure</option>
        </select>
      </div>
      <div className="mt-8">
        <label htmlFor="message" className="text-xs uppercase tracking-[0.2em] font-bold text-navy">Briefly describe your legal matter</label>
        <textarea
          id="message"
          data-testid="form-message"
          required
          rows={4}
          value={form.message}
          onChange={update("message")}
          placeholder="Please provide a short overview. Avoid including highly confidential information at this stage."
          className={`${inputClass} resize-y`}
        />
      </div>
      <p className="text-xs text-muted-foreground mt-6">
        Submitting this form does not create an attorney-client relationship.
      </p>
      <button
        type="submit"
        data-testid="form-submit-button"
        className="group w-full mt-6 inline-flex items-center justify-center gap-3 rounded-full bg-navy text-cream text-sm font-bold px-8 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
      >
        Request Consultation
        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};

const Contact = () => (
  <>
    <section className="bg-navy pt-32 pb-20 md:pt-40 md:pb-28" data-testid="contact-hero">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Eyebrow light>Contact &amp; Consultation</Eyebrow>
        <h1 className="font-serif font-light text-cream text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[1.02]">
          <MaskedLine delay={0.1}>Tell us how</MaskedLine>
          <MaskedLine delay={0.25}><em className="italic text-gold">we can assist.</em></MaskedLine>
        </h1>
        <p className="text-cream/70 max-w-xl mt-8 text-base md:text-lg leading-relaxed">
          Contact Annwil Law Ja or complete the consultation request form below
          to tell us briefly about the legal matter you would like to discuss.
        </p>
      </div>
    </section>

    <section className="py-24 md:py-32" data-testid="contact-main">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-[1fr_1.2fr] gap-16">
        <div>
          <Reveal>
            <Eyebrow>Reach the Office</Eyebrow>
            <div className="space-y-8">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-gold-hover font-bold mb-2">Telephone</p>
                <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="font-serif text-2xl md:text-3xl text-navy hover:text-gold-hover transition-colors duration-300">
                  {CONTACT.phone}
                </a>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-gold-hover font-bold mb-2">Email</p>
                <a href={CONTACT.emailHref} data-testid="contact-email-link" className="font-serif text-2xl md:text-3xl text-navy hover:text-gold-hover transition-colors duration-300 break-all">
                  {CONTACT.email}
                </a>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-gold-hover font-bold mb-2">Office</p>
                <p className="font-serif text-2xl md:text-3xl text-navy leading-snug">
                  {CONTACT.addressLines[0]}
                  <br />
                  {CONTACT.addressLines[1]}
                </p>
                <a
                  href={CONTACT.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-link"
                  className="group inline-flex items-center gap-2 mt-3 text-sm font-bold uppercase tracking-[0.2em] text-navy border-b border-gold pb-1 transition-colors duration-300 hover:text-gold-hover"
                >
                  <MapPin size={14} />
                  Get Directions
                </a>
              </div>
              <div data-testid="contact-diaspora-note" className="border border-gold/40 bg-sand/60 p-5">
                <p className="text-sm text-navy leading-relaxed">
                  <span className="font-bold">Living overseas?</span> Most of our
                  clients do. Consultations are easily arranged by telephone or
                  video call — wherever in the world you are.
                </p>
                <a
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="inline-flex items-center gap-2 mt-3 text-sm font-bold text-navy border-b border-gold pb-0.5 transition-colors duration-300 hover:text-gold-hover"
                >
                  Quick question? Chat with us on WhatsApp
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="mt-14">
            <Eyebrow>What Happens Next</Eyebrow>
            <div>
              {NEXT_STEPS.map((step) => (
                <div key={step.number} className="border-t border-navy/15 py-6 grid grid-cols-[64px_1fr] gap-4 items-baseline">
                  <span className="font-serif italic text-gold-hover text-2xl">{step.number}.</span>
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl text-navy">{step.title}</h3>
                    <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <ConsultationForm />
          <div className="flex flex-wrap gap-6 mt-8 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><Phone size={14} className="text-gold-hover" /> Prefer to call? {CONTACT.phone}</span>
            <span className="flex items-center gap-2"><Mail size={14} className="text-gold-hover" /> {CONTACT.email}</span>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Contact;
